import React, { useState, ChangeEvent, FormEvent, useCallback } from 'react';
import Button from 'components/Button';
import { moderationService } from 'api/services/ModerationService';
import { ReportContentType } from 'api/types/moderation';

export const COMPLAINT_CATEGORY_LABELS: Record<string, string> = {
  misleading: 'Yanıltıcı İçerik/Bilgi',
  fake: 'Sahte Ürün',
  scam: 'Dolandırıcılık',
  unresponsive: 'Satıcı İletişim Kurmuyor',
  wrong_category: 'Yanlış Kategori',
  inappropriate: 'Uygunsuz İçerik',
  other: 'Diğer',
};

export interface ReportModalProps {
  modalId?: string;
  /** Listing/reel id; omit for Profile (`contentType` Profile). */
  contentId?: string | null;
  contentType: ReportContentType;
  reportedUserId: string | undefined;
  /** When false, submit shows login prompt via onRequireLogin. */
  isAuthenticated: boolean;
  onRequireLogin?: () => void;
  /** Optional: cannot report own content */
  isOwnContent?: boolean;
  title?: string;
}

function closeBootstrapModal(modalId: string): void {
  if (typeof document === 'undefined') return;
  const closeBtn = document.querySelector(`#${modalId} .btn-close`) as HTMLButtonElement | null;
  closeBtn?.click();
}

const ReportModal: React.FC<ReportModalProps> = ({
  modalId = 'complaintModal',
  contentId,
  contentType,
  reportedUserId,
  isAuthenticated,
  onRequireLogin,
  isOwnContent = false,
  title = 'İlan Şikayeti',
}) => {
  const defaultTitle =
    contentType === ReportContentType.Profile
      ? 'Profil Şikayeti'
      : contentType === ReportContentType.Reel
        ? 'Reel Şikayeti'
        : 'İlan Şikayeti';
  const modalTitle = title === 'İlan Şikayeti' && contentType !== ReportContentType.Listing ? defaultTitle : title;

  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setCategory('');
    setDescription('');
    setAlsoBlock(false);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (isOwnContent) {
      window.alert('Kendi içeriğinizi şikayet edemezsiniz.');
      return;
    }
    if (!isAuthenticated) {
      onRequireLogin?.();
      return;
    }
    if (!reportedUserId) {
      window.alert('Şikayet gönderilemedi: eksik bilgi.');
      return;
    }
    if (contentType !== ReportContentType.Profile && !contentId) {
      window.alert('Şikayet gönderilemedi: içerik kimliği eksik.');
      return;
    }
    const label = COMPLAINT_CATEGORY_LABELS[category] ?? category;
    const message = `[Sebep: ${label}]\n\n${description.trim()}`;

    setSubmitting(true);
    try {
      const result = await moderationService.createReport({
        reportedUserId,
        contentType,
        contentId:
          contentType === ReportContentType.Profile ? null : contentId ?? null,
        message,
        alsoBlock,
      });
      if (result.isSuccess) {
        window.alert('Şikayetiniz moderatörlerimize iletilmiştir. Teşekkür ederiz.');
        resetForm();
        closeBootstrapModal(modalId);
      } else {
        window.alert(result.message ?? 'Şikayet gönderilemedi.');
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal fade" id={modalId} tabIndex={-1}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-exclamation-circle text-primary me-2"></i>
              {modalTitle}
            </h5>
            <Button className="btn-close" data-bs-dismiss="modal" type="button"></Button>
          </div>
          <div className="modal-body">
            <form id={`${modalId}Form`} onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-medium">Şikayet Sebebi</label>
                <select
                  className="form-select"
                  required
                  value={category}
                  onChange={(ev: ChangeEvent<HTMLSelectElement>) => setCategory(ev.target.value)}
                  disabled={submitting}
                >
                  <option value="">Seçiniz...</option>
                  <option value="misleading">Yanıltıcı İçerik/Bilgi</option>
                  <option value="fake">Sahte Ürün</option>
                  <option value="scam">Dolandırıcılık</option>
                  <option value="unresponsive">Satıcı İletişim Kurmuyor</option>
                  <option value="wrong_category">Yanlış Kategori</option>
                  <option value="inappropriate">Uygunsuz İçerik</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-medium">Detaylı Açıklama</label>
                <textarea
                  className="form-control"
                  placeholder="Lütfen şikayetinizi detaylı bir şekilde açıklayın..."
                  required
                  rows={4}
                  value={description}
                  onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => setDescription(ev.target.value)}
                  disabled={submitting}
                />
                <div className="form-text">
                  Şikayetiniz gizli tutulacak ve yalnızca yetkili moderatörler tarafından incelenecektir.
                </div>
              </div>
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`${modalId}-also-block`}
                  checked={alsoBlock}
                  onChange={(ev) => setAlsoBlock(ev.target.checked)}
                  disabled={submitting || isOwnContent}
                />
                <label className="form-check-label" htmlFor={`${modalId}-also-block`}>
                  Bu kullanıcıyı da engelle
                </label>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <Button className="btn btn-secondary" data-bs-dismiss="modal" type="button" disabled={submitting}>
              İptal
            </Button>
            <Button
              className="btn btn-primary"
              form={`${modalId}Form`}
              type="submit"
              disabled={submitting || isOwnContent}
            >
              <i className="fas fa-exclamation-circle me-2"></i>
              {submitting ? 'Gönderiliyor…' : 'Şikayet Et'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
